import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { parsePostsPlatform, PostsPlatform } from '../postsModel';

export interface PostsFilters {
  /** The song filter of the Posts page (`?catalogItemUuid=`); the release tab passes its own song. */
  catalogItemUuid: string | undefined;
  platform: PostsPlatform | undefined;
  /** The file open in the viewer (`?asset=`). */
  asset: string | undefined;
  view: 'board' | 'archive';
  outcome: 'posted' | 'never' | undefined;
  setSong: (catalogItemUuid: string | undefined) => void;
  setPlatform: (platform: PostsPlatform | undefined) => void;
  setAsset: (asset: string | undefined) => void;
  setView: (view: 'board' | 'archive') => void;
  setOutcome: (outcome: 'posted' | 'never' | undefined) => void;
}

/**
 * The filters of the Posts page live in the address (design assets-rethink c1): `catalogItemUuid`,
 * `platform`, `asset`, `view=archive`, `outcome`. Every other value of the address stays (the
 * release page keeps `catalogItemUuid` and `tab`). `fixedSong` is the song of the release Posts tab.
 */
export function usePostsFilters(fixedSong?: string): PostsFilters {
  const [params, setParams] = useSearchParams();

  const update = useCallback(
    (name: string, value: string | undefined, replace = false) => {
      setParams(
        (current) => {
          const next = new URLSearchParams(current);
          if (value === undefined || value === '') {
            next.delete(name);
          } else {
            next.set(name, value);
          }
          return next;
        },
        { replace }
      );
    },
    [setParams]
  );

  const song = params.get('catalogItemUuid') ?? '';
  const asset = params.get('asset') ?? '';
  const outcome = params.get('outcome');

  return useMemo(
    () => ({
      catalogItemUuid: fixedSong ?? (song === '' ? undefined : song),
      platform: parsePostsPlatform(params.get('platform')),
      asset: asset === '' ? undefined : asset,
      view: params.get('view') === 'archive' ? 'archive' : 'board',
      outcome: outcome === 'posted' || outcome === 'never' ? outcome : undefined,
      setSong: (value) => update('catalogItemUuid', value),
      setPlatform: (value) => update('platform', value),
      setAsset: (value) => update('asset', value, value === undefined),
      setView: (value) => update('view', value === 'archive' ? 'archive' : undefined),
      setOutcome: (value) => update('outcome', value),
    }),
    [asset, fixedSong, outcome, params, song, update]
  );
}
